import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  criarCompraEventoEspecial,
  obterEventoEspecialPorSlug,
  validarCupomEventoEspecial,
  verificarStatusCompraEventoEspecial,
} from '@/api/checkout';
import { copiarTexto } from '@/shared/utils/copiarTexto';

import {
  CARTAO_INICIAL,
  ESTADO_INICIAL,
  validarCPF,
} from './EventoEspecialModel';

export function useEventoEspecialViewModel() {
  const { slug } = useParams();

  const [evento, setEvento] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [naoEncontrado, setNaoEncontrado] = useState(false);

  const [dados, setDados] = useState(ESTADO_INICIAL);
  const [cartao, setCartao] = useState(CARTAO_INICIAL);
  const [metodoPagamento, setMetodoPagamento] = useState('pix');
  const [erros, setErros] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [erroGlobal, setErroGlobal] = useState('');
  const [resultado, setResultado] = useState(null);
  // null = não tentou | true = copiou | false = falhou (a view mostra o código)
  const [pixCopiado, setPixCopiado] = useState(null);
  const [confirmado, setConfirmado] = useState(false);
  const [termosAceitos, setTermosAceitos] = useState(false);
  // Cupom já validado: { codigo, descontoCentavos }.
  const [cupom, setCupom] = useState(null);
  const validarCupom = (codigo) => validarCupomEventoEspecial({ codigo, slug });

  useEffect(() => {
    setCarregando(true);
    obterEventoEspecialPorSlug(slug)
      .then((data) => {
        setEvento(data);
        if (data.pixOnly) setMetodoPagamento('pix');
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNaoEncontrado(true);
      })
      .finally(() => setCarregando(false));
  }, [slug]);

  // Polling do status enquanto aguarda confirmação do PIX
  useEffect(() => {
    if (confirmado || !resultado?.purchaseId || resultado?.paid) return;

    let tentativas = 0;
    const interval = setInterval(async () => {
      tentativas++;
      if (tentativas >= 20) {
        clearInterval(interval);
        return;
      }
      try {
        const data = await verificarStatusCompraEventoEspecial(
          resultado.purchaseId,
        );
        if (data.paid) {
          setConfirmado(true);
          clearInterval(interval);
        }
      } catch {
        /* silencioso */
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [resultado, confirmado]);

  function atualizarDado(campo, valor) {
    if (erros[campo]) setErros((prev) => ({ ...prev, [campo]: null }));
    setDados((prev) => ({ ...prev, [campo]: valor }));
  }

  function atualizarCartao(campo, valor) {
    setCartao((prev) => ({ ...prev, [campo]: valor }));
  }

  function validar() {
    const e = {};
    if (!dados.nome.trim()) e.nome = 'Nome obrigatório';
    if (!dados.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dados.email))
      e.email = 'E-mail inválido';
    const cpfLimpo = dados.cpf.replace(/\D/g, '');
    if (cpfLimpo.length !== 11 || !validarCPF(cpfLimpo)) e.cpf = 'CPF inválido';
    const whatsLimpo = dados.whatsapp.replace(/\D/g, '');
    if (whatsLimpo.length < 10) e.whatsapp = 'WhatsApp inválido';
    if (!termosAceitos) e.termos = 'É necessário aceitar os termos';
    if (metodoPagamento === 'cartao') {
      if (!cartao.numero.replace(/\s/g, '')) e.cartaoNumero = 'Obrigatório';
      if (!cartao.nome.trim()) e.cartaoNome = 'Obrigatório';
      if (!cartao.validade.trim()) e.cartaoValidade = 'Obrigatório';
      if (!cartao.cvv.trim()) e.cartaoCvv = 'Obrigatório';
    }
    setErros(e);
    return Object.keys(e).length === 0;
  }

  async function enviarCompra(ev) {
    ev.preventDefault();
    if (!validar()) return;
    setEnviando(true);
    setErroGlobal('');

    const [mes, ano] = (cartao.validade || '').split('/');
    try {
      const data = await criarCompraEventoEspecial({
        slug,
        nome: dados.nome.trim(),
        email: dados.email.trim(),
        cpf: dados.cpf,
        whatsapp: dados.whatsapp,
        paymentMethod: metodoPagamento === 'cartao' ? 'CREDIT_CARD' : 'PIX',
        termsAccepted: termosAceitos,
        cupom: cupom?.codigo,
        card:
          metodoPagamento === 'cartao'
            ? {
                number: cartao.numero,
                holderName: cartao.nome,
                expiryMonth: mes,
                expiryYear: ano,
                ccv: cartao.cvv,
              }
            : undefined,
      });

      if (data.paid) setConfirmado(true);
      setResultado(data);
    } catch (err) {
      const mensagem =
        err?.response?.data?.error ??
        'Não foi possível concluir o pagamento. Tente novamente.';
      setErroGlobal(mensagem);
      // O cupom pode ter esgotado ou vencido entre a prévia e o pagamento.
      if (mensagem.startsWith('Cupom')) setCupom(null);
    } finally {
      setEnviando(false);
    }
  }

  async function copiarPix() {
    const copiou = await copiarTexto(resultado?.pixCopiaECola);
    setPixCopiado(copiou);
    if (copiou) setTimeout(() => setPixCopiado(null), 2500);
  }

  return {
    evento,
    carregando,
    naoEncontrado,
    dados,
    atualizarDado,
    cartao,
    atualizarCartao,
    metodoPagamento,
    setMetodoPagamento,
    erros,
    enviando,
    erroGlobal,
    resultado,
    confirmado,
    termosAceitos,
    setTermosAceitos,
    enviarCompra,
    copiarPix,
    pixCopiado,
    cupom,
    setCupom,
    validarCupom,
  };
}
